import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { RoleService } from '../../users/services/RoleService';

type UserRole = 'admin' | 'manager' | 'employee' | 'client';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isPlatformAdmin: boolean;
}

/**
 * Custom hook for accessing authentication state and actions.
 * Wraps Clerk authentication to provide a unified interface.
 */
export function useAuth() {
  const { isSignedIn, isLoaded } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [roleError, setRoleError] = useState<string | null>(null);

  // Fetch user role from database when user is authenticated
  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn || !clerkUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    const fetchUserRole = async () => {
      try {
        // Check if user is a platform admin via Clerk metadata
        const isPlatformAdmin = (clerkUser.publicMetadata?.is_platform_admin === true);

        // If user is platform admin, set role to admin and skip database query
        if (isPlatformAdmin) {
          setUser({
            id: clerkUser.id,
            email: clerkUser.emailAddresses[0]?.emailAddress || '',
            name: clerkUser.fullName || '',
            role: 'admin',
            isPlatformAdmin: true,
          });
          setRoleError(null);
          setLoading(false);
          return;
        }

        // Otherwise, try to fetch role from database
        // Note: This requires clerk_user_id to be properly mapped in the database
        const roleService = new RoleService();
        const { role, error } = await roleService.getUserRole();

        if (error) {
          console.warn('Failed to fetch user role from database:', error);
          // Default to employee role if database lookup fails
          setUser({
            id: clerkUser.id,
            email: clerkUser.emailAddresses[0]?.emailAddress || '',
            name: clerkUser.fullName || '',
            role: 'employee',
            isPlatformAdmin: false,
          });
          setRoleError(null);
          setLoading(false);
          return;
        }

        setUser({
          id: clerkUser.id,
          email: clerkUser.emailAddresses[0]?.emailAddress || '',
          name: clerkUser.fullName || '',
          role: (role as UserRole) || 'employee',
          isPlatformAdmin: false,
        });
        setRoleError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to fetch user role';
        console.error('Error in fetchUserRole:', message);
        // Set a default user with employee role on error
        setUser({
          id: clerkUser.id,
          email: clerkUser.emailAddresses[0]?.emailAddress || '',
          name: clerkUser.fullName || '',
          role: 'employee',
          isPlatformAdmin: false,
        });
        setRoleError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [isLoaded, isSignedIn, clerkUser]);

  return {
    user,
    isAuthenticated: isSignedIn,
    loading,
    error: roleError,
    profileLoading: false,
    profileError: null,
    login: () => {}, // Clerk handles login via SignIn component
    logout: () => {}, // Clerk handles logout via UserButton
    updateProfile: async () => {}, // Not implemented for Clerk
    refreshProfile: async () => {}, // Clerk handles this automatically
    checkSession: () => {}, // Clerk handles this automatically
    clearError: () => {}, // Clerk handles errors internally
  };
}